/**
 * @file    serializerDelta.ts
 * @scope   SHARED
 * @summary Enterprise serializer delta: plugs the existing Enterprise storage hooks into the
 *          engine serialization core.
 * @consumes widgetSerializer / widgetDeserializer hooks (in place, not moved)
 * @produces ENTERPRISE_PAYLOAD_VERSION, migrateEnterpriseConfig, ENTERPRISE_SERIALIZER_DELTA
 * @sideEffects None.
 * @notes   v1 = today's payload. The defensive fallbacks inside `widgetDeserializer` ARE the v1
 *          reader; `migrate` is the explicit seam where any future v1 → v2 upgrade goes, instead of
 *          adding another layer of `??`.
 */
import { EnterpriseGlobalModel } from 'models/Enterprise/EnterpriseGlobalModel';
import type { SerializerDelta } from '../../engine/contracts';
import {
  getEnterpriseCollections,
  serializeEnterpriseCollection,
} from './utils/storage/widgetSerializer';
import {
  deserializeEnterpriseCollections,
  emptyEnterpriseSlice,
} from './utils/storage/widgetDeserializer';

export const ENTERPRISE_PAYLOAD_VERSION = 1;

/**
 * Upgrades an Enterprise config written with an older payload version.
 * @param config Persisted config.
 * @param fromVersion Version found in the envelope.
 * @returns The config in the current version's shape (identity while only v1 exists).
 */
export const migrateEnterpriseConfig = (config: unknown, fromVersion: number): unknown => {
  // WHY: no version below 1 exists yet; add one `if (fromVersion < N)` step per future bump.
  void fromVersion;
  return config;
};

export const ENTERPRISE_SERIALIZER_DELTA: SerializerDelta<EnterpriseGlobalModel> = {
  version: ENTERPRISE_PAYLOAD_VERSION,
  getCollections: getEnterpriseCollections,
  serializeCollection: serializeEnterpriseCollection,
  deserializeCollections: deserializeEnterpriseCollections,
  emptySlice: emptyEnterpriseSlice,
  migrate: migrateEnterpriseConfig,
};
